import { useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  ENTITY_FILE_UPLOAD_CONFIG,
  filterFilesByAllowedMime,
  type EntityFileUploadCategory,
} from "@/react/components/entityViews/files/constants/entityFileUpload";

export function useEntityFileUpload({
  category,
  uploadNewFile,
}: {
  category: EntityFileUploadCategory;
  uploadNewFile: (files: FileList | File[]) => Promise<void>;
}) {
  const uploadRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const { accept, rejectionMessage } = ENTITY_FILE_UPLOAD_CONFIG[category];

  const openUpload = useCallback(() => {
    uploadRef.current?.click();
  }, []);

  const handleUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!event.target.files?.length) return;

      const { accepted, rejectedCount } = filterFilesByAllowedMime(event.target.files, category);
      if (rejectedCount > 0) {
        toast.error(rejectionMessage);
      }
      if (!accepted.length) {
        event.target.value = "";
        return;
      }

      setIsUploading(true);
      try {
        await uploadNewFile(accepted);
      } finally {
        event.target.value = "";
        setIsUploading(false);
      }
    },
    [category, rejectionMessage, uploadNewFile],
  );

  return { uploadRef, isUploading, openUpload, handleUpload, accept };
}
